import type { ElementRef } from '../../../../lib/teact/teact';
import { getIsHeavyAnimating, useEffect, useRef } from '../../../../lib/teact/teact';

import { requestMeasure } from '../../../../lib/fasterdom/fasterdom';
import { releasePlaybackSlot, requestPlaybackSlot } from '../../../../util/videoPlaybackBudget';

import useHeavyAnimation from '../../../../hooks/useHeavyAnimation';
import useLastCallback from '../../../../hooks/useLastCallback';
import usePriorityPlaybackCheck, { isPriorityPlaybackActive } from '../../../../hooks/usePriorityPlaybackCheck';
import useBackgroundMode, { isBackgroundModeActive } from '../../../../hooks/window/useBackgroundMode';

export default function useVideoAutoPause(
  playerRef: ElementRef<HTMLVideoElement>, canPlay: boolean, isPriority?: boolean,
) {
  const canPlayRef = useRef();
  canPlayRef.current = canPlay;

  // A video whose turn to play comes up long after it asked must re-check that it still wants to
  // play — by then the app may have been backgrounded, or the video scrolled out of view
  const canResumePlayback = useLastCallback(() => Boolean(canPlayRef.current) && (isPriority || !isFrozen()));

  const { play, pause } = usePlayPause(playerRef, canResumePlayback, isPriority);

  const unfreezePlaying = useLastCallback(() => {
    if (canPlayRef.current && (isPriority || !isFrozen())) {
      play();
    }
  });

  const unfreezePlayingOnRaf = useLastCallback(() => {
    requestMeasure(unfreezePlaying);
  });

  useBackgroundMode(pause, unfreezePlayingOnRaf, !canPlay || isPriority);
  useHeavyAnimation(pause, unfreezePlaying, !canPlay || isPriority);
  usePriorityPlaybackCheck(pause, unfreezePlaying, !canPlay || isPriority);

  const handlePlaying = useLastCallback(() => {
    if (!canPlayRef.current || (!isPriority && isFrozen())) {
      pause();
    }
  });

  useEffect(() => {
    if (canPlay) {
      if (isPriority || !isFrozen()) {
        play();
      }
    } else {
      pause();
    }
  }, [canPlay, play, pause, isPriority]);

  return { handlePlaying };
}

function usePlayPause(
  mediaRef: ElementRef<HTMLMediaElement>, canResumePlayback: () => boolean, isPriority?: boolean,
) {
  const shouldPauseRef = useRef(false);
  const isLoadingPlayRef = useRef(false);

  const resume = useLastCallback(() => {
    if (canResumePlayback()) play();
  });

  const play = useLastCallback(() => {
    shouldPauseRef.current = false;
    const mediaEl = mediaRef.current;
    if (!mediaEl || isLoadingPlayRef.current || !document.body.contains(mediaEl)) return;

    // Media the user opened deliberately never waits for a slot; background playback (GIF grids,
    // autoplaying videos in the message list) does, and resumes through this same callback
    if (!isPriority && mediaEl instanceof HTMLVideoElement && !requestPlaybackSlot(mediaEl, resume)) return;

    isLoadingPlayRef.current = true;
    mediaEl.play().then(() => {
      isLoadingPlayRef.current = false;
      if (shouldPauseRef.current) {
        mediaRef.current?.pause();
        shouldPauseRef.current = false;
      }
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn(e);
    });
  });

  const pause = useLastCallback(() => {
    const mediaEl = mediaRef.current;
    if (mediaEl instanceof HTMLVideoElement) {
      releasePlaybackSlot(mediaEl);
    }

    if (isLoadingPlayRef.current) {
      shouldPauseRef.current = true;
    } else {
      mediaEl?.pause();
    }
  });

  // An unmounting video is never paused through `pause`, so it would hold its slot forever
  useEffect(() => {
    const mediaEl = mediaRef.current;

    return () => {
      if (mediaEl instanceof HTMLVideoElement) {
        releasePlaybackSlot(mediaEl);
      }
    };
  }, [mediaRef]);

  return { play, pause };
}

function isFrozen() {
  return getIsHeavyAnimating() || isPriorityPlaybackActive() || isBackgroundModeActive();
}
